import { GroupPurchaseTable } from '@organisms/GroupPurchaseTable';
import {
  useGetCommunity,
  useGetEventByCommunityId,
  useGetGroupPromoBySlug,
} from '@sunrise-ui/api-client';
import {
  Colors,
  TextBold,
  TextReg,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

type Props = {
  slug?: string;
};

export default function GroupPurchaseView({ slug }: Props) {
  const styles = useDynamicStyles(createStyles);

  const {
    data,
    isLoading: isLoadingPromos,
    isError,
  } = useGetGroupPromoBySlug(slug);

  // Fetch community to get the community slug for event landing page URLs
  const { data: community, isLoading: isLoadingCommunity } = useGetCommunity(
    data?.community_id,
  );

  const { data: event, isLoading: isLoadingEvent } = useGetEventByCommunityId(
    community?.id,
  );
  const isLoading = isLoadingPromos || isLoadingCommunity || isLoadingEvent;

  const allPromos = data?.promos ?? [];

  const registeredCount = allPromos.filter((promo) => {
    const receipt = promo.receipts?.[0];
    return receipt?.user_id != null;
  }).length;
  const totalCount = allPromos.length;

  const ticketCountText = isLoading
    ? '—/— tickets registered'
    : `${registeredCount}/${totalCount} tickets registered`;

  if (!slug) {
    return (
      <View style={styles.errorContainer}>
        <TextReg style={styles.errorText}>
          Invalid link. Please check your email for the correct URL.
        </TextReg>
      </View>
    );
  }

  if (isError) {
    return (
      <View style={styles.errorContainer}>
        <TextReg style={styles.errorText}>
          Unable to load ticket information. Please try again.
        </TextReg>
      </View>
    );
  }

  return (
    <ScrollView
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.header}>
        <TextBold style={styles.title}>My Tickets</TextBold>
        <TextReg style={styles.subtitle}>{ticketCountText}</TextReg>
      </View>

      <View style={styles.tableContainer}>
        <GroupPurchaseTable
          promos={allPromos}
          isLoading={isLoading}
          communitySlug={community?.slug}
          eventId={event?.id}
        />
      </View>
    </ScrollView>
  );
}

const createStyles = ({ isMobile, isTablet }: WithResponsive) =>
  StyleSheet.create({
    scrollContent: {
      paddingBottom: 40,
    },
    header: {
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: isMobile ? 16 : isTablet ? 20 : 24,
      paddingTop: isMobile ? 16 : 24,
      paddingBottom: 16,
    },
    title: {
      fontSize: isMobile ? 24 : 32,
      color: Colors.text,
    },
    subtitle: {
      fontSize: isMobile ? 14 : 16,
      color: Colors.text,
      opacity: 0.7,
    },
    tableContainer: {
      paddingHorizontal: isMobile ? 16 : isTablet ? 20 : 24,
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: isMobile ? 16 : 24,
    },
    errorText: {
      fontSize: 16,
      color: Colors.text,
      textAlign: 'center',
    },
  });
